<template>
  <div class="staff-performance-table">
    <div v-if="!staff || staff.length === 0" class="empty-state">
      <i class="fas fa-users"></i>
      <p>No staff performance data available</p>
    </div>

    <div v-else class="table-container">
      <table class="staff-table">
        <thead>
          <tr>
            <th class="rank-col">#</th>
            <th>Cashier</th>
            <th class="text-right">Orders</th>
            <th class="text-right">Revenue</th>
            <th class="text-right">AOV</th>
            <th class="text-right">Discounts</th>
            <th class="text-right">Refunds</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(member, index) in displayStaff" :key="member.user_id" class="staff-row">
            <td class="rank-col">
              <div class="rank-badge" :class="getRankClass(index)">
                <i v-if="index < 3" :class="getTrophyIcon(index)"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
            </td>

            <td>
              <div class="staff-info">
                <a class="staff-name" :href="staffLink(member)">{{ member.name }}</a>
                <div class="staff-metrics-mini">
                  <span class="metric-badge">{{ member.orders }} orders</span>
                  <span v-if="member.discount_percent > 10" class="metric-badge warning">
                    {{ member.discount_percent }}% disc
                  </span>
                </div>
              </div>
            </td>

            <td class="text-right">
              <a class="orders-value" :href="staffLink(member)">{{ formatNumber(member.orders, 0) }}</a>
            </td>

            <td class="text-right">
              <span class="revenue-value">KES {{ formatNumber(member.revenue) }}</span>
            </td>

            <td class="text-right">
              <span class="aov-value">KES {{ formatNumber(member.avg_order_value) }}</span>
            </td>

            <td class="text-right">
              <div class="discount-cell">
                <span class="discount-amount">KES {{ formatNumber(member.discounts_given) }}</span>
                <span class="discount-percent" :class="getDiscountClass(member.discount_percent)">
                  {{ member.discount_percent }}%
                </span>
              </div>
            </td>

            <td class="text-right">
              <div class="refunds-cell">
                <span class="refund-count">{{ member.refunds }}</span>
                <span v-if="member.voids > 0" class="void-count">({{ member.voids }} voids)</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="staff.length > limit" class="table-footer">
        <button @click="showAll = !showAll" class="btn-show-more">
          {{ showAll ? 'Show Top 10' : `Show All ${staff.length} Staff` }}
          <i :class="showAll ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StaffPerformanceTable',

  props: {
    staff: {
      type: Array,
      required: true
    },
    limit: {
      type: Number,
      default: 10
    },
    startDate: {
      type: String,
      default: ''
    },
    endDate: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      showAll: false
    };
  },

  computed: {
    displayStaff() {
      return this.showAll ? this.staff : this.staff.slice(0, this.limit);
    }
  },

  methods: {
    staffLink(member) {
      return `/employees?emp_id=${member.employee_id || member.user_id}&start-date=${this.startDate}&end-date=${this.endDate}`;
    },

    formatNumber(value, decimals = 2) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    },

    getRankClass(index) {
      if (index === 0) return 'rank-gold';
      if (index === 1) return 'rank-silver';
      if (index === 2) return 'rank-bronze';
      return '';
    },

    getTrophyIcon(index) {
      if (index === 0) return 'fas fa-trophy';
      if (index === 1) return 'fas fa-medal';
      if (index === 2) return 'fas fa-award';
      return '';
    },

    getDiscountClass(percent) {
      if (percent > 10) return 'high';
      if (percent > 5) return 'medium';
      return 'low';
    }
  }
};
</script>

<style scoped>
.staff-performance-table {
  height: 100%;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.table-container {
  overflow-x: auto;
}

.staff-table {
  width: 100%;
  border-collapse: collapse;
}

.staff-table thead {
  border-bottom: 2px solid #e2e8f0;
}

.staff-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.staff-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s;
}

.staff-table tbody tr:hover {
  background: #f8fafc;
}

.staff-table td {
  padding: 14px 16px;
}

.rank-col {
  width: 60px;
  text-align: center !important;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 14px;
  background: #f1f5f9;
  color: #64748b;
}

.rank-gold {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
}

.rank-silver {
  background: linear-gradient(135deg, #e5e7eb 0%, #9ca3af 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(156, 163, 175, 0.3);
}

.rank-bronze {
  background: linear-gradient(135deg, #fb923c 0%, #ea580c 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(251, 146, 60, 0.3);
}

.staff-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.staff-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
  text-decoration: none;
}

.staff-name:hover,
.orders-value:hover {
  color: #1e293b;
  text-decoration: underline;
}

.staff-metrics-mini {
  display: flex;
  gap: 6px;
}

.metric-badge {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #64748b;
  font-weight: 600;
}

.metric-badge.warning {
  background: #fef3c7;
  color: #d97706;
}

.text-right {
  text-align: right;
}

.orders-value {
  text-decoration: none;
}

.orders-value,
.aov-value {
  font-weight: 600;
  color: #475569;
  font-size: 14px;
}

.revenue-value {
  font-weight: 700;
  color: #1e293b;
  font-size: 14px;
}

.discount-cell,
.refunds-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.discount-amount {
  font-weight: 600;
  color: #64748b;
  font-size: 13px;
}

.discount-percent {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

.discount-percent.low {
  background: #dcfce7;
  color: #16a34a;
}

.discount-percent.medium {
  background: #fef3c7;
  color: #d97706;
}

.discount-percent.high {
  background: #fee2e2;
  color: #dc2626;
}

.refund-count {
  font-weight: 600;
  color: #64748b;
  font-size: 13px;
}

.void-count {
  font-size: 11px;
  color: #94a3b8;
}

.table-footer {
  padding: 16px;
  text-align: center;
  border-top: 1px solid #e2e8f0;
}

.btn-show-more {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-show-more:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
</style>
